import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Flex,
  Form,
  FormItemProps,
  FormProps,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from "antd";
import { addOrUpdateLocation, getLocation } from "api/services/LocationService";
import { TLocation, TRegionPicklist } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { CollapsibleGroup } from "components/CollapsibleGroup";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { ReactComponent as TickSquareLinear } from "Icons/vuesax/tick-square.svg";
import { useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
// @ts-ignore
import "react-phone-number-input/style.css";

type LocationFields = Omit<TLocation, "status">;

type TFormRow = {
  key: number | string;
  gutter: number;
  columns: (FormItemProps & { key: string | number })[];
};

type TFormGroup = {
  key: string | number;
  title?: string; 
  rows: TFormRow[];
};

const Header = ({
  location,
  onSubmit,
  onClose
}: {
  location?: TLocation;
  onSubmit: () => void;
  onClose: () => void;
}) => (
  <Flex wrap gap="middle" align="end" justify="space-between" style={{ marginBottom: "16px" }}>
    <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
      {!location ? "Add Location" : "Edit Location"}
    </Typography.Title>
    <Space size="small">
      <Button
        type="primary"
        htmlType="submit"
        className="default-button primary-btn"
        onClick={onSubmit}>
        <TickSquareLinear className="default-icon" />
        <span>Save</span>
      </Button>
      <Button
        style={{ border: "none" }}
        icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
        onClick={e => onClose()}></Button>
    </Space>
  </Flex>
);

const makeFields = (regions: TRegionPicklist[]) => {
  const regionOptions = regions.map(r => ({ value: r.regionId, label: r.regionName }));

  const fields: TFormGroup[] = [
      {
        key: "basic-info",
        title: "Basic Info",
        rows: [
          {
            key: "row-1",
            gutter: 8,
            columns: [
              {
                name: "locationName",
                label: "Name",
                key: 1,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ required: true, min: 3, max: 50, message: "Please enter Name" }],
              },
              {
                name: "locationShortName",
                label: "Short Name",
                key: 2,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ required: true, min: 3, max: 20, message: "Please enter Short Name" }],
              },
            ]
          }
        ]
      },
      {
        key: "address-info",
        title: "Address Info",
        rows: [
          {
            key: "row-1",
            gutter: 8,
            columns: [
              {
                name: "address1",
                label: "Address",
                key: 1,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ required: true, max: 250, message: "Please enter Address" }],
              },
              {
                name: "address2",
                label: "Suite",
                key: 2,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ max: 250 }],
              },
            ]
          },
          {
            key: "row-2",
            gutter: 8,
            columns: [
              {
                name: "city",
                label: "City",
                key: 1,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ required: true, max: 50, message: "Please enter City" }],
              },
              {
                name: "state",
                label: "State",
                key: 2,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [{ required: true, max: 2, message: "Please enter State" }],
              },
              {
                name: "zip",
                label: "Zip",
                key: 3,
                children: (
                  <Input
                    type="number"
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                )
              },
            ]
          },
          {
            key: "row-3",
            gutter: 8,
            columns: [
              {
                name: "regionId",
                label: "Region",
                key: 1,
                children: (
                  <Select
                    options={regionOptions.sort((a, b) => a.label.localeCompare(b.label))}
                    className="filter-input-transparent"
                    variant="borderless"
                  />
                ),
                rules: [{ required: true, max: 25, message: "Please select Region" }],
              },
            ]
          },
        ]
      },
      {
        key: "contact-info",
        title: "Contact Info",
        rows: [
          {
            key: "row-1",
            gutter: 8,
            columns: [
              {
                name: "phone1",
                label: "Phone",
                key: 1,
                children: (
                  <PhoneInput
                    defaultCountry="US"
                    className="PhoneInput"
                    onChange={() => { }}
                  />
                ),
                rules: [
                  { required: true, message: "Phone number is required!" },
                  {
                    validator: (_, value) => {
                      if (!value || isValidPhoneNumber(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Please enter a valid phone number!"));
                    },
                  },
                ]
              },
              {
                name: "fax",
                label: "Fax",
                key: 2,
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
                rules: [
                  {
                    required: true,
                    pattern: /^\d{10}$/,
                    message: "Fax is not valid!",
                    max: 20
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        key: "location-identifiers",
        title: "Location Identifiers",
        rows: [
          {
            key: "row-1",
            gutter: 8,
            columns: [
              {
                name: "extLocationId",
                label: "Customer Location Id",
                key: 1,
                rules: [{ required: true, min: 2, message: "Please enter Customer Location ID" }],
                children: (
                  <Input
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                  />
                ),
              },
              {
                name: "phoneLocationId",
                label: "Phone-In Location Id",
                key: 2,
                rules: [
                  {
                    pattern: /^\d{1,10}$/,
                    message: "Must be up to 10 digits"
                  }
                ],
                children: (
                  <Input
                    style={{ width: "100%" }}
                    autoComplete="false"
                    aria-autocomplete="none"
                    className="filter-input-transparent"
                    maxLength={10}
                    max={9999999999}
                  />
                ),
              }
            ]
          }
        ]
      }
    ]
    return fields;
};

type TAddOrUpdateLocationProps = {
  location?: TLocation;
  onClose: (location?: TLocation) => void;
};

const AddOrUpdateLocation = ({ location, onClose }: TAddOrUpdateLocationProps) => {
  const { accountId } = useTDAuthentication();
  const { regions } = usePicklists();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();
  const [localLocation, setLocalLocation] = useState<Partial<TLocation>>();

  const [form] = Form.useForm();
  useEffect(() => {
    if (accountId && location != null) {
      setLoading(true);
      setError(null);
      getLocation(accountId, location.locationId)
        .then(setLocalLocation)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (localLocation) {
      form.setFieldsValue(localLocation);
    }
  }, [localLocation]);

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(e => console.log(e));
  };
  const onFinish: FormProps<LocationFields>["onFinish"] = values => {
    setLoading(true);
    setError(null);
    addOrUpdateLocation(
      accountId!,
      { ...values, locationId: location?.locationId } as TLocation,
      location == null
    )
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      width={700}
      maskClosable={false}
      title={<Header location={location} onSubmit={onSave} onClose={onClose} />}
      open={true}
      footer={null}
      closeIcon={null}
      centered={true}
      onCancel={() => onClose()}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}

      {error && <ErrorViewer error={error} />}

      {!loading && (
        <Form
          form={form}
          labelAlign="right"
          name="add_or_update_location"
          onFinish={onFinish}
          validateTrigger={["onChange", "onBlur"]}
          size="small"
          style={{ maxHeight: "810px", overflowY: "auto"}}
          layout="vertical">
          {makeFields(regions!).map(group => (
            <CollapsibleGroup key={group.key} title={group.title}>
              {group.rows.map(row => (
                <Row gutter={[16, 24]} key={row.key}>
                  {row.columns.map(col => (
                    <Col key={col.key} xs={24} sm={12} md={8} lg={8}>
                      <Form.Item
                        key={col.name}
                        name={col.name}
                        label={
                          <label htmlFor={col.name} className="input-label">
                            {col.label}
                          </label>
                        }
                        rules={col.rules}
                        style={{ marginBottom: 24 }}>
                        {col.children}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              ))}
            </CollapsibleGroup>
          ))}
        </Form>
      )}
    </Modal>
  );
};

export default AddOrUpdateLocation;
